import mixGenerarPdf from '@/helpers/mixGenerarPdf';
import mixFormatoEstructura from '@/helpers/mixFormatoEstructura';

// Constantes para tipos de partida
const TIPO_PARTIDA = {
  RAIZ: 0,
  SUBTOTAL: 1,
  NIVEL_JERARQUICO: 11,
};

export default {
  name: 'MainReporteEstimacionesRelacionConceptos',
  mixins: [mixGenerarPdf, mixFormatoEstructura],
  data() {
    return {};
  },
  methods: {
    sumaImporteContratado(estimado) {
      const importes = estimado.map((est) => ({
        importe: est.concepto.importe,
      }));
      return this.$ObjSumar(importes, 'importe');
    },
    formatoObjeto(objeto) {
      return this.formatoEstructura(objeto);
    },

    /**
     * Crea un nivel jerárquico de partidas basado en el nivel anterior
     * @param {Array} nivelAnterior - Partidas del nivel hijo
     * @param {Map} catalogoIndex - Índice del catálogo de partidas
     * @param {Boolean} esNivelBase - Si es el primer nivel (usa conceptosEstimados)
     * @returns {Array} Partidas del nivel padre con sumas calculadas
     */
    crearNivelPartidas(nivelAnterior, catalogoIndex, esNivelBase = false) {
      // Obtener IDs de padres únicos
      const idsPadres = [...new Set(nivelAnterior.map(({ idPadre }) => idPadre))];

      return idsPadres
        .filter((id) => catalogoIndex.has(id))
        .map((idPadre) => {
          const partida = catalogoIndex.get(idPadre);
          const hijos = nivelAnterior.filter((p) => p.idPadre === idPadre);

          let cantidadAnterior = 0;
          let importeAnterior = 0;
          let cantidadAcumulada = 0;
          let importeAcumulado = 0;

          if (esNivelBase) {
            // Para el nivel base, sumar desde conceptosEstimados
            const conceptosHijos = hijos
              .map(({ conceptosEstimados }) => conceptosEstimados)
              .flat();
            cantidadAnterior = this.$ObjSumar(conceptosHijos, 'cantidadEstimadaAnterior') || 0;
            importeAnterior = this.$ObjSumar(conceptosHijos, 'importeEstimadoAnterior') || 0;
            cantidadAcumulada = this.$ObjSumar(conceptosHijos, 'cantidadEstimadaAcumulada') || 0;
            importeAcumulado = this.$ObjSumar(conceptosHijos, 'importeEstimadoAcumulado') || 0;
          } else {
            // Para niveles superiores, sumar desde propiedades directas
            cantidadAnterior = this.$ObjSumar(hijos, 'cantidadEstimadaAnterior') || 0;
            importeAnterior = this.$ObjSumar(hijos, 'importeEstimadoAnterior') || 0;
            cantidadAcumulada = this.$ObjSumar(hijos, 'cantidadEstimadaAcumulada') || 0;
            importeAcumulado = this.$ObjSumar(hijos, 'importeEstimadoAcumulado') || 0;
          }

          return {
            ...partida,
            tipo: TIPO_PARTIDA.NIVEL_JERARQUICO,
            cantidadEstimadaAnterior: cantidadAnterior,
            importeEstimadoAnterior: importeAnterior,
            cantidadEstimadaAcumulada: cantidadAcumulada,
            importeEstimadoAcumulado: importeAcumulado,
            conceptosEstimados: [],
          };
        });
    },

    async generarPdfRelacionConceptos() {
      this.$emit('generando');
      const encabezadoReferencia = this
        .mixGeneraEncabezadosPdf(`${this.estimacion.fechaPresentacion} 00:00:0`, this.firmas);

      let contrato = {
        numero: this.contrato.numero,
        objeto: this.contrato.meta.slice(0, 950),
        contratista: this.contrato.contratista,
        fecha: this.contrato.fecha,
        fechaInicio: this.contrato.fechaInicio,
        fechaTermino: this.contrato.fechaTermino,
        importe: this.contrato.esObraFinanciada
          ? (this.contrato.importeCostoDirecto + this.contrato.modificado.importeCostoDirecto)
          : this.contrato.importe,
      };
      let estimacion = {
        numero: this.estimacion.numero,
        fechaDel: this.estimacion.fechaDel,
        fechaAl: this.estimacion.fechaAl,
        fechaPresentacion: this.estimacion.fechaPresentacion,
        fechaAutorizacion: this.estimacion.fechaAutorizacion,
      };
      const tituloTotales = this.contrato.esObraFinanciada
        ? 'IMPORTE COSTO DIRECTO'
        : 'IMPORTE ESTIMADO';
      let totales = {
        tituloTotales,
        sumaTotalContratado: this.sumaTotalContratado.importe,
        sumaTotalEstimadoAnterior: this.sumaTotalEstimadoAnterior.importe,
        sumaTotalEstimadoActual: this.sumaTotalEstimadoActual.importe,
        sumaTotalAcumulado: (this.sumaTotalEstimadoAnterior.importe || 0)
                            + (this.sumaTotalEstimadoActual.importe || 0),
      };
      let partidas = this.getPartidas;
      const partidasSubtotal = partidas.map((partida) => {
        const subtotalImporteContratado = this.sumaImporteContratado(
          partida.conceptosEstimados,
        ) || 0;
        const subtotalImporteEstimadoAnterior = this.$ObjSumar(
          partida.conceptosEstimados,
          'importeEstimadoAnterior',
        ) || 0;
        const subtotalImporte = this.$ObjSumar(
          partida.conceptosEstimados,
          'importe',
        ) || 0;
        const subtotalImporteAcumulado = (this.$ObjSumar(
          partida.conceptosEstimados,
          'importeEstimadoAnterior',
        ) || 0) + (this.$ObjSumar(partida.conceptosEstimados, 'importe') || 0);
        return {
          partida,
          subtotalImporteContratado,
          subtotalImporteEstimadoAnterior,
          subtotalImporte,
          subtotalImporteAcumulado,
        };
      });

      partidas = this.formatoObjeto(partidasSubtotal);
      estimacion = this.formatoObjeto(estimacion);
      contrato = this.formatoObjeto(contrato);
      const supervisor = this.getSupervisorEnRelacionConceptos || '';
      const supervisionExterna = this.getContratoSupervisionExternaEnRelacionConceptos
        ?.contratoRelacionado
        ?.contratista?.nombreRazonSocial || '';
      const firmasFuncionarios = encabezadoReferencia.firmas || [];
      const fechaActual = this.formatoFechaActual();
      totales = this.formatoObjeto(totales);

      const nombreTemplate = 'templateRelacionConceptos';

      await this.mixGenerarPdf({
        configuracion: {
          path: 'wepp/wep',
          PUPPETEER_CONFIGURACION: {
            landscape: true,
            format: 'A3',
            displayHeaderFooter: false,
            margin: { top: '0px', bottom: '0px' },
          },
          nombreTemplate,
        },
        data: {
          contrato,
          estimacion,
          partidas,
          supervisor,
          supervisionExterna,
          firmas: firmasFuncionarios,
          fechaActual,
          totales,
          footer: '',
          encabezadoReferencia,
        },
      }, `${this.contrato.numero}_RelacionConceptos_Estimacion_${this.estimacion.numero}`);
      this.$emit('terminando');
    },
  },
  computed: {
    /**
     * Índice del catálogo de partidas para acceso O(1)
     */
    catalogoPartidasIndex() {
      if (!this.conceptos) return new Map();
      return new Map(
        this.conceptos.map((partida) => [
          partida.id,
          {
            id: partida.id,
            idPadre: partida.idPadre,
            clave: partida.clave.slice(0, 15),
            concepto: partida.concepto,
            orden: partida.orden,
            padre: null,
            tipo: partida.tipo,
            conceptosEstimados: [],
          },
        ]),
      );
    },

    getCatalogoPartidas() {
      if (!this.conceptos) return [];
      return [...this.catalogoPartidasIndex.values()];
    },

    getPartidas() {
      if (!this.conceptos) return [];

      // 1. Filtrar conceptos válidos una sola vez
      const conceptosValidos = this.estimacion.conceptos.filter(
        ({ cantidad }) => cantidad !== 0,
      );

      if (conceptosValidos.length === 0) return [];

      // 2. Agrupar conceptos por partida padre en una sola pasada
      const conceptosPorPartida = new Map();
      const partidasMap = new Map();

      conceptosValidos.forEach((conceptoEstimado) => {
        const { concepto } = conceptoEstimado;
        const { padre } = concepto;
        const partidaId = padre.id;

        // Crear partida si no existe
        if (!partidasMap.has(partidaId)) {
          let partidaPadre = null;
          if (padre.padre && (
            padre.padre.tipo === TIPO_PARTIDA.RAIZ
            || padre.padre.tipo === TIPO_PARTIDA.SUBTOTAL
          )) {
            partidaPadre = padre.padre;
          }

          partidasMap.set(partidaId, {
            id: partidaId,
            idPadre: padre.idPadre,
            clave: padre.clave.slice(0, 15),
            concepto: padre.concepto,
            orden: concepto.orden,
            padre: partidaPadre,
            tipo: padre.tipo,
            conceptosEstimados: [],
          });
        }

        // Agregar concepto al grupo
        if (!conceptosPorPartida.has(partidaId)) {
          conceptosPorPartida.set(partidaId, []);
        }
        conceptosPorPartida.get(partidaId).push(conceptoEstimado);
      });

      // 3. Calcular estimaciones para cada partida
      const numeroEstimacionActual = this.estimacion.numero;
      const partidasEstimadas = [...partidasMap.values()].map((partida) => {
        const conceptosPartida = conceptosPorPartida.get(partida.id) || [];

        const conceptosConCalculos = conceptosPartida
          .map((conceptoEstimado) => {
            const { estimado } = conceptoEstimado.concepto;

            // Filtrar y sumar estimaciones anteriores
            let cantidadAnterior = 0;
            let importeAnterior = 0;

            if (estimado && estimado.length > 0) {
              estimado.forEach((est) => {
                if (est.estimacion.numero < numeroEstimacionActual) {
                  cantidadAnterior += est.cantidad || 0;
                  importeAnterior += est.importe || 0;
                }
              });
            }

            return {
              ...conceptoEstimado,
              cantidadEstimadaAnterior: cantidadAnterior,
              importeEstimadoAnterior: importeAnterior,
              cantidadEstimadaAcumulada: cantidadAnterior + (conceptoEstimado.cantidad || 0),
              importeEstimadoAcumulado: importeAnterior + (conceptoEstimado.importe || 0),
            };
          })
          .sort((a, b) => a.concepto.orden - b.concepto.orden);

        return {
          ...partida,
          conceptosEstimados: conceptosConCalculos,
        };
      });

      // 4. Construir jerarquía de niveles usando la función auxiliar
      const catalogoIndex = this.catalogoPartidasIndex;
      const todosLosNiveles = [];
      let nivelActual = partidasEstimadas;
      let esNivelBase = true;
      const MAX_NIVELES = 5;

      for (let i = 0; i < MAX_NIVELES; i += 1) {
        const nuevoNivel = this.crearNivelPartidas(nivelActual, catalogoIndex, esNivelBase);
        if (nuevoNivel.length === 0) break;

        todosLosNiveles.push(...nuevoNivel);
        nivelActual = nuevoNivel;
        esNivelBase = false;
      }

      // 5. Eliminar duplicados y combinar con partidas estimadas
      const partidasNivelesUnicas = [...new Map(
        todosLosNiveles.map((v) => [v.id, v]),
      ).values()];

      // 6. Ordenar por campo orden
      return [
        ...partidasNivelesUnicas,
        ...partidasEstimadas,
      ].sort((a, b) => a.orden - b.orden);
    },

    sumaTotalContratado() {
      const partidas = this.getPartidas;
      if (partidas.length === 0) return { importe: 0, iva: 0, total: 0 };

      const importe = partidas.reduce((suma, partida) => {
        const importePartida = partida.conceptosEstimados.reduce(
          (acc, est) => acc + (est.concepto.importe || 0),
          0,
        );
        return suma + importePartida;
      }, 0);

      const iva = this.$numberRound(importe * (this.contrato.porcentajeIva || 0));
      const total = this.$numberRound(importe + iva);

      return { importe, iva, total };
    },

    sumaTotalEstimadoAnterior() {
      const partidas = this.getPartidas;
      if (partidas.length === 0) return { importe: 0, iva: 0, total: 0 };

      const importe = partidas.reduce((suma, partida) => {
        const importePartida = this.$ObjSumar(
          partida.conceptosEstimados,
          'importeEstimadoAnterior',
        ) || 0;
        return suma + importePartida;
      }, 0);

      const iva = this.$numberRound(importe * (this.contrato.porcentajeIva || 0));
      const total = this.$numberRound(importe + iva);

      return { importe, iva, total };
    },

    sumaTotalEstimadoActual() {
      const partidas = this.getPartidas;
      if (partidas.length === 0) return { importe: 0, iva: 0, total: 0 };

      const importe = partidas.reduce((suma, partida) => {
        const importePartida = this.$ObjSumar(
          partida.conceptosEstimados,
          'importe',
        ) || 0;
        return suma + importePartida;
      }, 0);

      const iva = this.$numberRound(importe * (this.contrato.porcentajeIva || 0));
      const total = this.$numberRound(importe + iva);

      return { importe, iva, total };
    },

    getSupervisorEnRelacionConceptos() {
      if (!this.contrato?.responsables) return null;
      return this.contrato.responsables.find(
        (responsable) => responsable.principal === true,
      );
    },

    getContratoSupervisionExternaEnRelacionConceptos() {
      if (!this.contrato?.contratosRelacionados) return null;
      return this.contrato.contratosRelacionados.find(
        ({ esSupervisionExterna }) => esSupervisionExterna === true,
      );
    },
  },
};
